import { Flags, useFlags } from 'commons/hooks/useFlags';
import { BuiltInComponents, CRAFT_ROOT_ID } from 'craft/constants';
import { useEditor, useMultiNodes } from 'craft/hooks';
import { useFlowAncestor, useFormAncestor } from 'craft/state';
import { Action, CATEGORIES, HANDLER_TYPES, INTERACTION_TYPES, NavigateAction } from 'craft/types';

import { Interaction, interactions } from './constants';
import { getAvailableActions } from './getAvailableActions';

const handlerToInteractionType: Partial<Record<HANDLER_TYPES, INTERACTION_TYPES>> = {
  [HANDLER_TYPES.CHANGE_CONTENT]: INTERACTION_TYPES.Content,
  [HANDLER_TYPES.TRIGGER_MODAL]: INTERACTION_TYPES.TriggerModal,
  [HANDLER_TYPES.TRIGGER_HOTSPOT_GROUP]: INTERACTION_TYPES.TriggerHotspotGroup,
  [HANDLER_TYPES.TRIGGER_TOUR]: INTERACTION_TYPES.TriggerTour,
  [HANDLER_TYPES.DISMISS_TOUR]: INTERACTION_TYPES.DismissTour,
  [HANDLER_TYPES.DISMISS_AND_OPEN_NEXT_HOTSPOT]: INTERACTION_TYPES.DismissAndOpenNextHotspot,
  [HANDLER_TYPES.DISMISS_HOTSPOT_GROUP]: INTERACTION_TYPES.DismissHotspotGroup,
  [HANDLER_TYPES.DISMISS_HOTSPOT]: INTERACTION_TYPES.DismissHotspot,
  [HANDLER_TYPES.DISMISS_MODAL]: INTERACTION_TYPES.DismissModal,
  [HANDLER_TYPES.NAVIGATE]: INTERACTION_TYPES.URL,
  [HANDLER_TYPES.FORM_SUBMIT]: INTERACTION_TYPES.FormSubmit,
  [HANDLER_TYPES.TRIGGER_CLICK]: INTERACTION_TYPES.TriggerClick,
  [HANDLER_TYPES.CALLBACK]: INTERACTION_TYPES.Callback,
};

export const getInteractionFromAction = (action?: Action) => {
  if (!action) {
    return INTERACTION_TYPES.None;
  }
  if (action.category === CATEGORIES.DISMISS) {
    return INTERACTION_TYPES.Dismiss;
  }
  if (action.category === CATEGORIES.PENDO) {
    return INTERACTION_TYPES.Guide;
  }
  if (action.category === CATEGORIES.FLOW) {
    return INTERACTION_TYPES.Flow;
  }
  if (action.category === CATEGORIES.TOUR) {
    return INTERACTION_TYPES.Tour;
  }
  if (action.category === CATEGORIES.RICHTEXT) {
    return INTERACTION_TYPES.CopyRichText;
  }

  if ((action.handler as NavigateAction['handler'])?.href?.includes('mailto')) {
    return INTERACTION_TYPES.Email;
  }

  const interactionType = handlerToInteractionType[action.handler.type];
  if (interactionType) {
    return interactionType;
  }

  throw new Error(
    `Error when trying to render interaction form for action type ${action.handler.type}`,
  );
};

// Some interaction types are only allowed once per click
const singletonInteractions = [
  INTERACTION_TYPES.Flow,
  INTERACTION_TYPES.TriggerModal,
  INTERACTION_TYPES.DismissModal,
  INTERACTION_TYPES.Tour,
  INTERACTION_TYPES.DismissTour,
  INTERACTION_TYPES.DismissHotspotGroup,
  INTERACTION_TYPES.Content,
  INTERACTION_TYPES.FormSubmit,
  INTERACTION_TYPES.CopyRichText,
];

const isTourInteractionAllowed = (
  label: Interaction,
  existingActions: Action[],
  currentAction?: Action,
) => {
  // if we have an existing dismiss tour action, don't show tour step change in extra actions list
  if (label.category === CATEGORIES.TOUR) {
    return (
      getInteractionFromAction(currentAction) === INTERACTION_TYPES.DismissTour ||
      !existingActions.find((action) => {
        return getInteractionFromAction(action) === INTERACTION_TYPES.DismissTour;
      })
    );
  }

  // if we have an existing tour step change action, don't show dismiss tour in extra actions list
  if (label.id === INTERACTION_TYPES.DismissTour) {
    return (
      getInteractionFromAction(currentAction) === INTERACTION_TYPES.Tour ||
      !existingActions.find((action) => {
        return getInteractionFromAction(action) === INTERACTION_TYPES.Tour;
      })
    );
  }

  return true;
};

const filterSingletonInteractions = (
  label: Interaction,
  existingActions: Action[],
  currentAction?: Action,
) => {
  // don't exclude the current action
  if (currentAction && label.id === getInteractionFromAction(currentAction)) {
    return true;
  }

  // if this action isn't a singleton, early out
  if (!singletonInteractions.includes(label.id)) {
    return true;
  }

  // filter singletons that have a matching action && tour-specific singleton interactions (if any)
  return (
    !existingActions.find((action) => {
      return getInteractionFromAction(action) === label.id;
    }) && isTourInteractionAllowed(label, existingActions, currentAction)
  );
};

export const useAvailableInteractions = (nodeIds: string[], currentAction?: Action) => {
  const { customActions, type } = useMultiNodes(nodeIds, (node) => ({
    customActions: node?.data?.custom?.actions,
  }));

  const existingActions = (customActions || []) as Action[];

  const { isInForm, isMultistepForm } = useFormAncestor(nodeIds[0]);
  const { isInFlow } = useFlowAncestor(nodeIds[0]);
  const { query } = useEditor();

  const rootName = query.node(CRAFT_ROOT_ID)?.get()?.data?.name;

  const {
    [Flags.MODALS]: withModals,
    [Flags.TOURS]: withTours,
    [Flags.HOTSPOTS]: withHotspots,
    [Flags.TRIGGER_MODAL_ACTION]: withLegacyModals,
  } = useFlags();

  if (type === BuiltInComponents.FormSingleSelectCard) {
    return interactions.filter(
      (label) => label.id === INTERACTION_TYPES.Flow || label.id === INTERACTION_TYPES.None,
    );
  }

  return getAvailableActions({
    rootName,
    isInForm,
    isMultistepForm,
    isInFlow,
    withModals: !!withModals,
    withLegacyModals: !!withLegacyModals,
    withTours: !!withTours,
    withHotspots: !!withHotspots,
  }).filter((label) => filterSingletonInteractions(label, existingActions, currentAction));
};
